/**
 * Salon promotions on the booking site (mpgadmin G45, D30; CMS G11;
 * GUARDRAILS G9 here). The catalogue is public; mine, claim and the
 * summary's options are the signed-in client's, named by their token. Every
 * amount here is a preview: the server prices the booking again.
 */
import { CMS_BASE_URL, CMS_ENVIRONMENT_QUERY } from '../config/api.config';
import { getCMSJWT } from './auth.service';
import { storageService } from './storage.service';
import type { DealsPreview, Promotion, PromotionOption } from '../types/promotion.types';
import type { DealsPreviewBody } from '../utils/serviceDeals';

/** Offers dismissed with "Not now" on this device (ids). Personal: cleared on sign-out (G1). */
export const PROMO_DISMISSED_KEY = 'promo_popup_dismissed_v1';
/** An offer to claim once the visitor has signed in. Personal: cleared on sign-out (G1). */
export const PROMO_CLAIM_AFTER_SIGN_IN_KEY = 'promo_claim_after_sign_in_v1';

const CATALOG_TTL_MS = 5 * 60 * 1000;
const catalogCache = new Map<number, { at: number; data: Promotion[] }>();
/** The catalogue reads under way, by business: the bar and the deal tags ask as the step opens, and share one. */
const catalogReads = new Map<number, Promise<Promotion[]>>();
/** The read of the client's own offers under way (getMine). */
let mineRead: { jwt: string; promise: Promise<Promotion[]> } | null = null;

/** A booking refused over its promotion: the CMS's 409 `promotion_refused` (CMS G11). */
export const PROMOTION_REFUSED = 'promotion_refused';

export class PromotionRefusedError extends Error {
  readonly code = PROMOTION_REFUSED;
  constructor(message: string, readonly promotionId: number | null) {
    super(message);
    this.name = 'PromotionRefusedError';
  }
}

/** Checked by the code, so an error that crossed a boundary is still recognised. */
export const isPromotionRefusedError = (error: unknown): error is PromotionRefusedError =>
  (error as { code?: unknown } | null)?.code === PROMOTION_REFUSED;

/** The CMS's refusal from a failed booking response, or null. */
export const promotionRefusalFrom = (status: number, body: string): PromotionRefusedError | null => {
  if (status !== 409) return null;
  try {
    const parsed = JSON.parse(body);
    if (parsed?.code !== PROMOTION_REFUSED) return null;
    return new PromotionRefusedError(
      typeof parsed.error === 'string' && parsed.error ? parsed.error : "That offer can't be used on this booking.",
      Number.isInteger(parsed.promotionId) ? parsed.promotionId : null
    );
  } catch {
    return null;
  }
};

const authed = async (path: string, init: RequestInit = {}): Promise<Response> => {
  const jwt = getCMSJWT();
  if (!jwt) throw new Error('NOT_SIGNED_IN');
  return fetch(`${CMS_BASE_URL}${path}`, {
    cache: 'no-store',
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}`, ...(init.headers ?? {}) },
  });
};

const errorText = async (res: Response): Promise<string> => {
  try {
    const body = await res.json();
    return typeof body?.error === 'string' ? body.error : "That didn't work. Please try again.";
  } catch {
    return "That didn't work. Please try again.";
  }
};

const readList = <T,>(dismissed: string | null): T[] => {
  try {
    const parsed = JSON.parse(dismissed ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const promotionService = {
  /** The business's live offers (public). Empty when there are none, or on any error. */
  getCatalog(companyId: number): Promise<Promotion[]> {
    const hit = catalogCache.get(companyId);
    if (hit && Date.now() - hit.at < CATALOG_TTL_MS) return Promise.resolve(hit.data);
    const under = catalogReads.get(companyId);
    if (under) return under;
    const read = (async (): Promise<Promotion[]> => {
      try {
        const res = await fetch(
          `${CMS_BASE_URL}/api/promotions/catalog?companyId=${companyId}${CMS_ENVIRONMENT_QUERY}`
        );
        const data: Promotion[] = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : [];
        catalogCache.set(companyId, { at: Date.now(), data: list });
        return list;
      } catch {
        return [];
      } finally {
        catalogReads.delete(companyId);
      }
    })();
    catalogReads.set(companyId, read);
    return read;
  },

  /**
   * The signed-in client's own offers with progress. Empty when signed out.
   * Callers at the same moment share one read (the services step's cart and
   * its offers bar both ask as it opens); nothing is kept once it answers,
   * so a claim is always read back fresh.
   */
  getMine(): Promise<Promotion[]> {
    const jwt = getCMSJWT();
    if (!jwt) return Promise.resolve([]);
    if (mineRead?.jwt === jwt) return mineRead.promise;
    const promise = (async (): Promise<Promotion[]> => {
      try {
        const res = await authed('/api/promotions/mine');
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      } finally {
        if (mineRead?.jwt === jwt) mineRead = null;
      }
    })();
    mineRead = { jwt, promise };
    return promise;
  },

  /** Claim an offer. Throws with the salon's reason when it can't be theirs. */
  async claim(promotionId: number): Promise<Promotion> {
    const res = await authed(`/api/promotions/${promotionId}/claim`, { method: 'POST' });
    // A read of their offers begun before the claim answers without it: the next one starts afresh.
    mineRead = null;
    if (!res.ok) throw new Error(await errorText(res));
    return res.json();
  },

  /** The booking summary's choices, priced as a preview. Empty on any error: booking still works. */
  async getOptions(params: { storeId: string | number; startUtc: string; baseAmount: number; balanceCap: number }): Promise<PromotionOption[]> {
    if (!getCMSJWT()) return [];
    const q = new URLSearchParams({
      storeId: String(params.storeId),
      startUtc: params.startUtc,
      baseAmount: Math.max(0, params.baseAmount).toFixed(2),
      balanceCap: Math.max(0, params.balanceCap).toFixed(2),
    });
    try {
      const res = await authed(`/api/promotions/options?${q}`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },

  /**
   * The server's deals for a cart (mpgadmin G138). Null when signed out, on
   * any error, or from an API without deals: the booking then goes without
   * service deals, as the page shows it (nothing is guessed here).
   */
  async preview(body: DealsPreviewBody, signal?: AbortSignal): Promise<DealsPreview | null> {
    if (!getCMSJWT()) return null;
    try {
      const res = await authed('/api/promotions/preview', { method: 'POST', body: JSON.stringify(body), signal });
      if (!res.ok) return null;
      const data = await res.json();
      return data && Array.isArray(data.lines) && typeof data.total === 'number' ? (data as DealsPreview) : null;
    } catch {
      return null;
    }
  },

  dismissed(): number[] {
    return readList<number>(storageService.getItem(PROMO_DISMISSED_KEY));
  },

  dismiss(promotionId: number): void {
    const ids = new Set(this.dismissed());
    ids.add(promotionId);
    storageService.setItem(PROMO_DISMISSED_KEY, JSON.stringify(Array.from(ids).slice(-50)));
  },

  claimAfterSignIn(promotionId: number | null): void {
    if (promotionId) storageService.setItem(PROMO_CLAIM_AFTER_SIGN_IN_KEY, String(promotionId));
    else storageService.removeItem(PROMO_CLAIM_AFTER_SIGN_IN_KEY);
  },

  pendingClaim(): number | null {
    const id = Number(storageService.getItem(PROMO_CLAIM_AFTER_SIGN_IN_KEY));
    return Number.isInteger(id) && id > 0 ? id : null;
  },
};
